import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useMeQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import type { IRecordInfoFooterProps } from './IRecordInfoFooterProps';
import { useAgo } from '../tabs/useAgo';
import { useCopyFeedback } from './toolbar/useCopyFeedback';

/**
 * «Відомості про запис» (UIUX forms/record-information, прототип v4): GUID — клік копіює («✓ Скопійовано» на 1,6 с) · Модуль (повернення до
 * модуля, як page:'module' прототипу; запис дочірньої таблиці — сама таблиця: «Статті», як окремий модуль прототипу) і Робочий простір —
 * посилання · Автор — посилання на сторінку учасників (з ним у фокусі, як mbFocus прототипу) · Створено / Оновлено — відносний час (точний
 * у підказці) · Джерело: Людина або «✦ Написано ШІ» і «зміна».
 */
export function RecordInfoFooter({ ws, record, module, rt, editor, canEdit }: IRecordInfoFooterProps) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const ago = useAgo();
  const strings = useModuleStrings();
  const { data: me } = useMeQuery();
  const guid = useCopyFeedback();
  const workspace = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  const ai = record.ext?.['aiAuthored'] === true;
  return (
    <footer className="rec-info" aria-label={t('recordPage.info.title')}>
      <span className="rec-info__item"><b>{t('recordPage.info.guid')}:</b>
        <button type="button" className="rec-info__guid" title={t('recordPage.info.copyTitle')} aria-label={t('recordPage.info.copyLabel', { key: 'GUID' })} onClick={() => guid.copy(record.id)}>{record.id}</button>
        {guid.copied && <span className="rec-info__copied">{t('recordPage.info.copied')}</span>}
      </span>
      <span className="rec-info__item"><b>{t('recordPage.info.module')}:</b>
        {rt.main ? <Link to={routes.moduleReturn(module.module.key)}>{strings.moduleName(module.module.name)}</Link>
          : <Link to={routes.list(module.module.key, rt.key)}>{strings.entity(rt.key, 'name', rt.name)}</Link>}
      </span>
      {workspace && <span className="rec-info__item"><b>{t('recordPage.info.workspace')}:</b><Link to={routes.home()}>{workspace}</Link></span>}
      {record.createdBy && (
        <span className="rec-info__item"><b>{t('recordPage.info.createdBy')}:</b>
          <Link className="rec-info__person" to={`${routes.home()}/members?focus=${encodeURIComponent(record.createdBy.id)}`}>{record.createdBy.name}</Link>
        </span>
      )}
      <span className="rec-info__item" title={formatDateTime(record.createdAt, lang)}><b>{t('recordPage.info.created')}:</b><span>{ago(record.createdAt)}</span></span>
      <span className="rec-info__item" title={formatDateTime(record.updatedAt, lang)}><b>{t('recordPage.info.updated')}:</b><span>{ago(record.updatedAt)}</span></span>
      <span className="rec-info__item rec-info__item--origin"><b>{t('recordPage.info.origin')}:</b>
        {ai ? <span className="rec-info__ai" title={t('recordPage.info.aiTitle')}>{t('recordPage.info.ai')}</span> : <span>{t('recordPage.info.human')}</span>}
        {canEdit && <button type="button" className="rec-info__change" title={t('recordPage.info.changeTitle')} aria-label={t('recordPage.info.changeTitle')}
          onClick={() => void editor.patchNow({}, undefined, { aiAuthored: !ai })}>{t('recordPage.info.change')}</button>}
      </span>
    </footer>
  );
}
